import React, { useEffect, useMemo, useRef } from 'react';
import { enGB } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { trapTabKey } from 'components/common/calendar/calendarHelpers';

interface MonthGridPanelProps {
    selectedMonth: number;
    selectedYear:  number;
    onSelect: (month: number, year: number) => void;
    style?: React.CSSProperties;
    /** date-fns Locale used to localise the short month names. Defaults to enGB. */
    locale?: Locale;
}

export const MonthGridPanel = React.forwardRef<HTMLDivElement, MonthGridPanelProps>(
    ({ selectedMonth, selectedYear, onSelect, style, locale }, ref) => {
        const today      = new Date();
        const todayMonth = today.getMonth();
        const todayYear  = today.getFullYear();

        const loc = locale ?? enGB;
        const monthNames = useMemo(
            () => Array.from({ length: 12 }, (_, i) =>
                loc.localize!.month(i, { width: 'abbreviated' })
            ),
            [loc]
        );

        // The grid is portaled out of the popup, so opening it has to hand focus over
        // explicitly — Tab alone would never reach it.
        const panelRef = useRef<HTMLDivElement>(null);
        const selectedRef = useRef<HTMLButtonElement>(null);
        useEffect(() => selectedRef.current?.focus(), []);

        return (
            <div
                ref={element => {
                    panelRef.current = element;
                    if (typeof ref === 'function') ref(element);
                    else if (ref) ref.current = element;
                }}
                className="calendar-month-panel"
                style={style}
                onKeyDown={event => trapTabKey(event, panelRef.current)}
            >
                <div className="calendar-month-grid">
                    {monthNames.map((name, idx) => (
                        <button
                            key={idx}
                            ref={idx === selectedMonth ? selectedRef : undefined}
                            className={[
                                'calendar-month-item',
                                idx === selectedMonth ? 'calendar-month-item--selected' : '',
                                idx === todayMonth && todayYear === selectedYear &&
                                    idx !== selectedMonth ? 'calendar-month-item--today' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => onSelect(idx, selectedYear)}
                        >
                            {name}
                        </button>
                    ))}
                </div>
            </div>
        );
    }
);
MonthGridPanel.displayName = 'MonthGridPanel';